'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocale } from 'next-intl';

const POST_PREVIEW_HISTORY_KEY = 'botgoPostPreviewV1';
const PREVIEW_METADATA_FIELDS = Object.freeze([
  { selector: 'meta[name="description"]', tagName: 'meta', attribute: 'content', key: 'description', valueAttribute: 'name' },
  { selector: 'link[rel="canonical"]', tagName: 'link', attribute: 'href', key: 'canonical', valueAttribute: 'rel' },
  { selector: 'meta[property="og:type"]', tagName: 'meta', attribute: 'content', key: 'og:type', valueAttribute: 'property' },
  { selector: 'meta[property="og:title"]', tagName: 'meta', attribute: 'content', key: 'og:title', valueAttribute: 'property' },
  { selector: 'meta[property="og:description"]', tagName: 'meta', attribute: 'content', key: 'og:description', valueAttribute: 'property' },
  { selector: 'meta[property="og:url"]', tagName: 'meta', attribute: 'content', key: 'og:url', valueAttribute: 'property' },
  { selector: 'meta[property="og:image"]', tagName: 'meta', attribute: 'content', key: 'og:image', valueAttribute: 'property' },
  { selector: 'meta[name="twitter:title"]', tagName: 'meta', attribute: 'content', key: 'twitter:title', valueAttribute: 'name' },
  { selector: 'meta[name="twitter:description"]', tagName: 'meta', attribute: 'content', key: 'twitter:description', valueAttribute: 'name' },
  { selector: 'meta[name="twitter:image"]', tagName: 'meta', attribute: 'content', key: 'twitter:image', valueAttribute: 'name' },
]);

function currentRelativeUrl() {
  if (typeof window === 'undefined') return '/';
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function historyState() {
  if (typeof window === 'undefined') return {};
  const value = window.history.state;
  return value && typeof value === 'object' ? value : {};
}

function previewUrl(locale, postId, source) {
  return `/${locale}/post/${encodeURIComponent(postId)}?from=${encodeURIComponent(source)}`;
}

function capturePageMetadata() {
  return {
    title: document.title,
    fields: PREVIEW_METADATA_FIELDS.map((field) => {
      const element = document.head.querySelector(field.selector);
      return {
        ...field,
        existed: Boolean(element),
        value: element?.getAttribute(field.attribute) || '',
      };
    }),
  };
}

function setMetadataField(field, value) {
  if (!value) return;
  let element = document.head.querySelector(field.selector);
  if (!element) {
    element = document.createElement(field.tagName);
    element.setAttribute(field.valueAttribute, field.key);
    element.dataset.botgoPostPreviewMetadata = 'true';
    document.head.appendChild(element);
  }
  element.setAttribute(field.attribute, value);
}

function absoluteMediaUrl(value) {
  if (!value) return '';
  try {
    return new URL(value, window.location.origin).toString();
  } catch {
    return '';
  }
}

function applyPostMetadata(item, locale, snapshot) {
  const title = item?.title || item?.caption || 'AI creation on BotGo';
  const description = item?.description || item?.caption || 'Explore this public AI creation on BotGo.';
  let canonicalOrigin = window.location.origin;
  const originalCanonical = snapshot?.fields?.find((field) => field.key === 'canonical')?.value;
  try {
    if (originalCanonical) canonicalOrigin = new URL(originalCanonical).origin;
  } catch {}
  const canonical = `${canonicalOrigin}/${locale}/post/${encodeURIComponent(item.id)}`;
  const media = item?.media?.[0] || null;
  const image = absoluteMediaUrl(media?.thumbnailUrl || (media?.mediaType === 'image' ? media?.url : ''));
  const values = {
    description,
    canonical,
    'og:type': 'article',
    'og:title': `${title} | BotGo`,
    'og:description': description,
    'og:url': canonical,
    'og:image': image,
    'twitter:title': `${title} | BotGo`,
    'twitter:description': description,
    'twitter:image': image,
  };
  document.title = `${title} | BotGo`;
  PREVIEW_METADATA_FIELDS.forEach((field) => setMetadataField(field, values[field.key]));
}

function restorePageMetadata(snapshot) {
  if (!snapshot) return;
  document.title = snapshot.title;
  snapshot.fields.forEach((field) => {
    const element = document.head.querySelector(field.selector);
    if (!field.existed) {
      if (element?.dataset.botgoPostPreviewMetadata === 'true') element.remove();
      return;
    }
    if (element) element.setAttribute(field.attribute, field.value);
  });
}

/**
 * Keeps list-origin previews instant while still exposing the canonical Post URL.
 *
 * Direct requests to /post/:id continue to render the independent server route.
 * In-app list clicks reuse the Post data already present in the feed, update the
 * address bar with native History API integration, and never wait on an RSC or
 * database round trip before showing or dismissing the viewer.
 */
export default function useInstantPostPreview({ items = [], source }) {
  const locale = useLocale();
  const [activePostId, setActivePostId] = useState(null);
  const returnUrlRef = useRef(null);
  const metadataSnapshotRef = useRef(null);

  const activeIndex = useMemo(() => {
    if (!activePostId) return null;
    const index = items.findIndex((item) => item?.id === activePostId);
    return index >= 0 ? index : null;
  }, [activePostId, items]);

  const writePreviewEntry = useCallback((postId, method = 'replaceState') => {
    if (typeof window === 'undefined' || !postId) return;
    const existingMarker = historyState()[POST_PREVIEW_HISTORY_KEY];
    const returnUrl = existingMarker?.source === source
      ? existingMarker.returnUrl
      : returnUrlRef.current || currentRelativeUrl();
    returnUrlRef.current = returnUrl;
    window.history[method]({
      ...historyState(),
      [POST_PREVIEW_HISTORY_KEY]: {
        postId,
        source,
        returnUrl,
      },
    }, '', previewUrl(locale, postId, source));
  }, [locale, source]);

  const openPreview = useCallback((item) => {
    if (!item?.id || typeof window === 'undefined') return;
    const marker = historyState()[POST_PREVIEW_HISTORY_KEY];
    if (!metadataSnapshotRef.current) metadataSnapshotRef.current = capturePageMetadata();
    setActivePostId(item.id);
    applyPostMetadata(item, locale, metadataSnapshotRef.current);
    writePreviewEntry(
      item.id,
      marker?.source === source ? 'replaceState' : 'pushState',
    );
  }, [locale, source, writePreviewEntry]);

  const closePreview = useCallback(() => {
    setActivePostId(null);
    restorePageMetadata(metadataSnapshotRef.current);
    metadataSnapshotRef.current = null;
    if (typeof window === 'undefined') return;
    const marker = historyState()[POST_PREVIEW_HISTORY_KEY];
    const returnUrl = marker?.returnUrl || returnUrlRef.current;
    returnUrlRef.current = null;
    if (marker?.source === source) {
      window.history.back();
      return;
    }
    if (returnUrl) {
      window.history.replaceState(historyState(), '', returnUrl);
    }
  }, [source]);

  const setPreviewIndex = useCallback((nextIndex) => {
    if (nextIndex === null) {
      closePreview();
      return;
    }
    const nextItem = items[Number(nextIndex)];
    if (!nextItem?.id) return;
    if (!metadataSnapshotRef.current) metadataSnapshotRef.current = capturePageMetadata();
    setActivePostId(nextItem.id);
    applyPostMetadata(nextItem, locale, metadataSnapshotRef.current);
    writePreviewEntry(nextItem.id);
  }, [closePreview, items, locale, writePreviewEntry]);

  useEffect(() => {
    const restorePreviewFromHistory = (event) => {
      const marker = event.state?.[POST_PREVIEW_HISTORY_KEY];
      if (marker?.source === source) {
        const item = items.find((candidate) => candidate?.id === marker.postId);
        if (!metadataSnapshotRef.current) metadataSnapshotRef.current = capturePageMetadata();
        setActivePostId(item?.id || null);
        if (item) applyPostMetadata(item, locale, metadataSnapshotRef.current);
        returnUrlRef.current = marker.returnUrl;
        return;
      }
      setActivePostId(null);
      restorePageMetadata(metadataSnapshotRef.current);
      metadataSnapshotRef.current = null;
      returnUrlRef.current = null;
    };
    window.addEventListener('popstate', restorePreviewFromHistory);
    return () => window.removeEventListener('popstate', restorePreviewFromHistory);
  }, [items, locale, source]);

  return {
    activeIndex,
    closePreview,
    openPreview,
    setPreviewIndex,
  };
}
